import { Text } from '@/components/ui';
import { StatusBadge } from '@/features/contracts/components/StatusBadge';
import { Notification as NotificationData } from '@/lib/api/notifications';
import { differenceInDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import clsx from 'clsx';
import { Trans } from 'react-i18next';

interface NotificationProps {
  notification: NotificationData;
  onMarkAsRead?: (id: string) => void;
  isMarkingAsRead?: boolean;
}

export function Notification({ notification, onMarkAsRead, isMarkingAsRead }: NotificationProps) {
  const formattedDate = format(new Date(notification.createdAt), "dd 'de' MMMM", {
    locale: ptBR,
  });

  const handleClick = () => {
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div
      className={clsx(
        'border-transparent-dark-12 bg-base-white p-md gap-md flex items-start justify-between rounded-2xl border shadow-xs transition-all',
        !notification.isRead &&
          'bg-brand-subtle border-brand-subtle hover:bg-brand-muted cursor-pointer',
        isMarkingAsRead && 'opacity-50'
      )}
      onClick={handleClick}
    >
      <div className="flex-1">
        <Text size="md" strong className="gap-4xs flex items-center">
          <Trans
            i18nKey={notification.title}
            values={{ company: notification.contract?.company.name }}
          />
          <StatusBadge status={notification.type as any} />
        </Text>
        <Text className="text-neutral-muted">
          <Trans
            i18nKey={notification.content}
            values={{
              contract: notification.contract?.number,
              ...(notification.type === 'LOW_HOURS' && {
                hoursLeft:
                  (notification.contract?.contractedHours || 0) -
                  (notification.contract?.consumedHours || 0),
              }),
              ...(notification.type === 'HOURS_EXCEED' && {
                hoursExceeded: Math.abs(
                  (notification.contract?.contractedHours || 0) -
                    (notification.contract?.consumedHours || 0)
                ),
              }),
              ...(notification.type === 'EXPIRING' && {
                date: format(notification.contract?.endDate || new Date(), 'dd/MM/yyyy'),
                daysLeft:
                  differenceInDays(notification.contract?.endDate || new Date(), new Date()) + 1,
              }),
            }}
          />
        </Text>
      </div>
      <Text className="text-neutral-muted">{formattedDate}</Text>
    </div>
  );
}
